import { useState } from 'react';
import type { SurveyConfig, Question } from './types';
import { computeVisibleQuestions } from '../../lib/display-logic-bridge';
import ImageLightbox from '../ImageLightbox';
import SignaturePad from '../runner/SignaturePad';
import RichTextView from './RichTextView';
import { paginateQuestions } from './question-utils';

type Props = {
  config: SurveyConfig;
};

export default function PreviewPanel({ config }: Props) {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [lightboxSrc, setLightboxSrc] = useState<{ src: string; alt: string } | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  // 分页模式：有 pagebreak 时显示页码切换；无 pagebreak 时保持单页（沿用旧逻辑）
  const pages = paginateQuestions(config.questions);
  const hasPager = pages.length > 1;
  const [pageIndex, setPageIndex] = useState(0);

  const visibleIds = computeVisibleQuestions(config, answers);
  const visible = config.questions.filter((q) => visibleIds.includes(q.id));

  const preface = config.settings?.preface;
  const [prefaceChecked, setPrefaceChecked] = useState(false);
  const sigOnPreface = !!(config.settings?.signature?.enabled && (config.settings?.signature as any)?.onPreface);
  const prefaceExtraQs = (preface?.questions ?? []) as any[];
  const prefaceQs = [...prefaceExtraQs, ...visible.filter((q) => q.onPreface)];
  const prefaceActive = (!!(preface?.body || preface?.title || preface?.image) || prefaceQs.length > 0 || sigOnPreface) && !prefaceChecked;
  const missingPreface = prefaceQs.filter((q) => q.required && !isAnswered(q, answers[q.id]));
  const canAcknowledge = missingPreface.length === 0;
  const mainQsAll = visible.filter((q) => !q.onPreface);
  // 当前页题目（仅显示当前页的题；pagebreak 在切片中被消耗掉，不会出现在页内）
  const currentPageQs = hasPager ? (pages[pageIndex] ?? []).filter((q) => mainQsAll.some((mq) => mq.id === q.id)) : mainQsAll;
  const setAns = (qid: string, v: any) => setAnswers((a) => ({ ...a, [qid]: v }));

  return (
    <div className="preview">
      <div className="preview-head">
        <h1>{config.header.mainTitle || '未命名问卷'}</h1>
        {config.header.subTitle && <p>{config.header.subTitle}</p>}
      </div>

      {prefaceActive ? (
        <div className="preface-card">
          {(preface?.title || preface?.body || preface?.image) && (
            <>
              <h2>{preface?.title ?? '前置须知'}</h2>
              {preface?.image && <img src={preface.image} alt="前置页图片" className="preface-image" />}
              {preface?.body && <pre className="preface-body">{preface.body}</pre>}
            </>
          )}
          {prefaceQs.length > 0 && (
            <ol className="preview-questions" style={{ marginTop: 12 }}>
              {prefaceQs.map((q, i) => (
                <li key={q.id} className="pq-item">
                  <div className="pq-no">{i + 1}</div>
                  <div className="pq-main">
                    <div className="pq-title">
                      <RichTextView value={q.title} />
                      {q.required && <span className="req">*</span>}
                    </div>
                    {renderQuestionInput(q, answers[q.id], (v) => setAns(q.id, v), (src, alt) => setLightboxSrc({ src, alt }))}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {sigOnPreface && (
            <div className="sig-block">
              <h3>签署人签名</h3>
              <SignaturePad mode={config.settings?.signature?.mode ?? 'draw'} value={signature} onChange={setSignature} />
            </div>
          )}
          <label className={`checkbox ${!canAcknowledge ? 'is-disabled' : ''}`}>
            <input type="checkbox" checked={prefaceChecked} disabled={!canAcknowledge} onChange={(e) => e.target.checked && setPrefaceChecked(true)} />
            我已知晓
          </label>
          {!canAcknowledge && <div className="hint hint-bad">请先完成前置页中的所有必答题</div>}
        </div>
      ) : (
        <>
          <ol className="preview-questions">
            {currentPageQs.map((q, i) => (
              <li key={q.id} className={`pq-item ${q.type === 'statement' ? ' is-statement' : ''}${q.type === 'pagebreak' ? ' is-pagebreak' : ''}`}>
                <div className="pq-no">{q.type === 'statement' ? '★' : i + 1}</div>
                <div className="pq-main">
                  {q.type === 'statement' ? (
                    <div className="pq-statement-block">
                      <RichTextView value={q.title} variant="statement" />
                      {q.description && <div className="pq-desc">{q.description}</div>}
                    </div>
                  ) : (
                    <>
                      <div className="pq-title">
                        <RichTextView value={q.title} />
                        {q.required && <span className="req">*</span>}
                      </div>
                      {(q.image ?? q.descriptionImage) && (
                        <img src={q.image ?? q.descriptionImage} alt="" style={{ maxWidth: 220, maxHeight: 140, borderRadius: 8, display: 'block', margin: '6px 0' }} />
                      )}
                      {q.description && <div className="pq-desc">{q.description}</div>}
                      {renderQuestionInput(q, answers[q.id], (v) => setAns(q.id, v), (src, alt) =>
                        setLightboxSrc({ src, alt }),
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
            {currentPageQs.length === 0 && <li className="pq-empty">{hasPager ? `第 ${pageIndex + 1} 页无可见题目` : '无可见题目（条件未满足或均在前置页）'}</li>}
          </ol>

          {hasPager && (
            <div className="runner-pager">
              <button
                type="button"
                className="btn"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              >
                ← 上一页
              </button>
              <span className="runner-pager-info">第 {pageIndex + 1} 页 / 共 {pages.length} 页</span>
              {pageIndex < pages.length - 1 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setPageIndex((i) => Math.min(pages.length - 1, i + 1))}
                >
                  下一页 →
                </button>
              ) : (
                <button type="button" className="btn btn-primary" disabled title="预览模式无提交">
                  提交（预览模式无提交）
                </button>
              )}
            </div>
          )}
        </>
      )}

      {!prefaceActive && !sigOnPreface && config.settings?.signature?.enabled && !hasPager && (
        <div className="sig-block">
          <h3>电子签（{config.settings.signature.mode === 'draw' ? '手写' : '上传'}）</h3>
          <SignaturePad
            mode={config.settings.signature.mode ?? 'draw'}
            value={signature}
            onChange={setSignature}
          />
        </div>
      )}

      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc.src}
          alt={lightboxSrc.alt}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </div>
  );
}

function normalizeOptions(q: Question): Array<{ value: string; image?: string }> {
  return (q.options ?? []).map((o) =>
    typeof o === 'string' ? { value: o } : { value: o.value, image: o.image },
  );
}

function isAnswered(q: { type?: string; params?: any }, value: any): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (q.type === 'matrix') {
    const rows: string[] = q.params?.rows ?? [];
    return !!value && typeof value === 'object' && rows.every((row) => value[row] !== undefined && value[row] !== null && value[row] !== '');
  }
  if (q.type === 'file') return !!(typeof value === 'object' && (value.dataUrl || value.attachment));
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function renderQuestionInput(
  q: Question,
  value: any,
  onChange: (v: any) => void,
  onImageClick: (src: string, alt: string) => void,
) {
  switch (q.type) {
    case 'text':
      return (
        <input
          className="pq-input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="请输入内容"
        />
      );
    case 'longtext': {
      const maxLen = (q.params as any)?.maxLen;
      return (
        <textarea
          className="pq-input pq-textarea"
          rows={3}
          maxLength={maxLen}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="请输入内容"
        />
      );
    }
    case 'single': {
      const opts = normalizeOptions(q);
      return (
        <div className="pq-radio-cards">
          {opts.map((o) => {
            const checked = value === o.value;
            return (
              <label key={o.value} className={`pq-radio-card ${checked ? 'is-checked' : ''}`}>
                <div className="pq-radio-meta">
                  <span className="dot" />
                  <input
                    type="radio"
                    name={`pq-${q.id}`}
                    checked={checked}
                    onChange={() => onChange(o.value)}
                  />
                  <span className="pq-radio-label">{o.value}</span>
                </div>
                {o.image && (
                  <button
                    type="button"
                    className="pq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onImageClick(o.image!, o.value);
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={o.image} alt={o.value} className="pq-opt-img" />
                  </button>
                )}
              </label>
            );
          })}
        </div>
      );
    }
    case 'multi': {
      const opts = normalizeOptions(q);
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="pq-radio-cards">
          {opts.map((o) => {
            const checked = arr.includes(o.value);
            return (
              <label key={o.value} className={`pq-radio-card ${checked ? 'is-checked' : ''}`}>
                <div className="pq-radio-meta">
                  <span className="dot" />
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      if (e.target.checked) onChange([...arr, o.value]);
                      else onChange(arr.filter((x: string) => x !== o.value));
                    }}
                  />
                  <span className="pq-radio-label">{o.value}</span>
                </div>
                {o.image && (
                  <button
                    type="button"
                    className="pq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onImageClick(o.image!, o.value);
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={o.image} alt={o.value} className="pq-opt-img" />
                  </button>
                )}
              </label>
            );
          })}
        </div>
      );
    }
    case 'slider': {
      const p: any = q.params ?? { min: 0, max: 100, step: 1, allowDecimal: true };
      const num = typeof value === 'number' ? value : p.min;
      return (
        <div className="pq-slider">
          <div className="pq-slider-labels">
            <span className="pq-slider-label-min">{p.minLabel || ''}</span>
            <span className="pq-slider-label-max">{p.maxLabel || ''}</span>
          </div>
          <input
            type="range"
            min={p.min}
            max={p.max}
            step={p.step}
            value={num}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <span className="pq-slider-val">
            {num}
            {p.unit ?? ''}
          </span>
        </div>
      );
    }
    case 'select': {
      return (
        <div className="select" style={{ maxWidth: 320 }}>
          <select value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
            <option value="">请选择</option>
            {(q.options ?? []).map((raw: any, i: number) => {
              const v = typeof raw === 'string' ? raw : raw.value;
              return <option key={i} value={v}>{v}</option>;
            })}
          </select>
        </div>
      );
    }
    case 'matrix': {
      const rows: string[] = (q.params as any)?.rows ?? [];
      const cols: string[] = (q.params as any)?.columns ?? [];
      const map = value && typeof value === 'object' ? value : {};
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 320 }}>
            <thead>
              <tr><th></th>{cols.map((c) => <th key={c} style={{ padding: '4px 8px', fontWeight: 500, fontSize: 12 }}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r}>
                  <td style={{ padding: '6px 8px', fontSize: 13, whiteSpace: 'nowrap' }}>{r}</td>
                  {cols.map((c) => (
                    <td key={c} style={{ textAlign: 'center', padding: '6px 8px' }}>
                      <input type="radio" name={`pv-${q.id}-${r}`} checked={map[r] === c} onChange={() => onChange({ ...map, [r]: c })} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'file':
      return <div className="pq-input-box" style={{ color: 'var(--text-2)' }}>填写端可上传附件（图片 / PDF）</div>;
    default:
      return null;
  }
}